import { memo, useCallback } from "react";
import CellButton from "@/components/CellButton";
import { OrbitSupportProjectSelect } from "@/components/OrbitSupportProjectSelect";
import { useChangeTaskCustomAttributeMutation } from "@/mutations/task-mutations";
import type { Attribute } from "@/types/attribute";
import type { ColumnDef } from "../../ColumnModel";
import type { Row } from "../../RowModel";

// Static functions - don't need to be recreated
const getSortValue = ({ row }: { row: Row }) => {
    return row.data.title; // todo: implement (we need to add a secondary "value" like description to the saved values, so we can sort on that. (in orbit customer the descripotionn would then be a "name"))
};

const getGroupHeader = () => {
    throw new Error("Not implemented. Custom attributes cannot be grouped for now.");
};

// Memoized cell component
const OrbitSupportProjectCell = memo(function OrbitSupportProjectCell({
    row,
    attribute,
    onValueChange,
}: {
    row: Row;
    attribute: Attribute;
    onValueChange: (taskId: number, attributeId: number, value: any) => void;
}) {
    const customFieldValues = row.data.customAttributeValues || []; // this can be null for new rows
    const customFieldValue = customFieldValues.find((x: any) => x.id === attribute.id);

    const handleChange = useCallback(
        (value: any) => {
            onValueChange(row.data.id, attribute.id as number, value);
        },
        [row.data.id, attribute.id, onValueChange]
    );

    return (
        <OrbitSupportProjectSelect
            trigger={(content) => <CellButton disabled={row.data.isNewlyCreated}>{content}</CellButton>}
            value={customFieldValue?.value}
            onChange={handleChange}
        />
    );
});

export default function useOrbitSupportProjectColumnBuilder() {
    const { mutate } = useChangeTaskCustomAttributeMutation();

    // Stable mutation handler
    const handleValueChange = useCallback(
        (taskId: number, attributeId: number, value: any) => {
            mutate({
                id: taskId,
                customAttributeId: attributeId,
                value: value,
            });
        },
        [mutate]
    );

    return {
        build: useCallback(
            (isVisible: boolean, attribute: Attribute): ColumnDef => {
                return {
                    id: attribute.id.toString(),
                    header: attribute.name,
                    isVisible: isVisible,
                    cssColDef: "150px",
                    isRight: false,
                    isReadOnly: false,
                    getSortValue: getSortValue,
                    getGroupHeader: getGroupHeader,
                    cell: ({ row }: { row: Row }) => <OrbitSupportProjectCell row={row} attribute={attribute} onValueChange={handleValueChange} />,
                };
            },
            [handleValueChange]
        ),
    };
}
